"use client";

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import useHeaderOffset from '@/hooks/useHeaderOffset';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Input } from '@/components/ui/input';
import { Loader2, X } from '@/components/icons';
import { cn } from '@/lib/utils';
import { CITY_LANDMARK_LOCATIONS } from '@/lib/defaultLocations';
import { supabase } from '@/lib/supabase/client';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useScrollLock } from '@/hooks/useScrollLock';

interface SelectedCityLocation {
  city: string;
  displayName: string;
  latitude: number;
  longitude: number;
  isPredefined?: boolean;
}

interface CitySelectorSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  selectedCity: string;
  onSelectCity: (selection: SelectedCityLocation) => void;
}

interface NominatimSearchResult {
  place_id: number;
  lat: string;
  lon: string;
  display_name: string;
  category?: string;
  type?: string;
  addresstype?: string;
  address?: {
    city?: string;
    town?: string;
    village?: string;
    hamlet?: string;
    municipality?: string;
    suburb?: string;
    county?: string;
    state?: string;
    country?: string;
  };
}

interface GlobalLocationResult {
  id: string;
  city: string;
  country: string;
  displayName: string;
  fullDisplayName: string;
  latitude: number;
  longitude: number;
}

interface CatalogCityOption {
  city: string;
  country: string;
  displayName: string;
  latitude: number;
  longitude: number;
  aliases?: string[];
}


const normalizeLocationText = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

export const CitySelectorSidebar: React.FC<CitySelectorSidebarProps> = ({
  isOpen,
  onClose,
  selectedCity,
  onSelectCity,
}) => {
  const { headerHeight, isDesktop } = useHeaderOffset();
  const [searchTerm, setSearchTerm] = useState('');
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [globalResults, setGlobalResults] = useState<GlobalLocationResult[]>([]);
  const [isSearchingGlobal, setIsSearchingGlobal] = useState(false);
  const [storeBackedCities, setStoreBackedCities] = useState<CatalogCityOption[]>([]);

  useScrollLock(isOpen, false, scrollAreaRef);

  const extractCityName = (item: NominatimSearchResult): string => {
    const fromAddress =
      item.address?.city ||
      item.address?.town ||
      item.address?.village ||
      item.address?.hamlet ||
      item.address?.municipality ||
      item.address?.suburb ||
      item.address?.county ||
      item.address?.state;

    if (fromAddress) return fromAddress;
    return item.display_name.split(',')[0]?.trim() || 'Ubicacion';
  };

  useEffect(() => {
    if (!isOpen) return;

    let mounted = true;

    const loadStoreCities = async () => {
      try {
        const { data, error } = await supabase
          .from('stores')
          .select('location_display_name, latitude, longitude')
          .not('location_display_name', 'is', null)
          .limit(500);

        if (error) {
          console.warn('[CitySelectorSidebar] Could not load store-backed cities', error);
          return;
        }

        const baseKeys = new Set(
          CITY_LANDMARK_LOCATIONS.map((locationItem) =>
            `${normalizeLocationText(locationItem.city)}|${normalizeLocationText(locationItem.country)}`
          )
        );

        const deduped = new Map<string, CatalogCityOption>();

        for (const row of data || []) {
          const displayName = (row.location_display_name || '').trim();
          const city = displayName.split(',')[0]?.trim();
          if (!city) continue;

          const parts = displayName
            .split(',')
            .map((part) => part.trim())
            .filter(Boolean);

          const country = parts.length > 1 ? parts[parts.length - 1] : 'Otros';
          const latitude = Number(row.latitude);
          const longitude = Number(row.longitude);
          if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) continue;

          const key = `${normalizeLocationText(city)}|${normalizeLocationText(country)}`;
          if (baseKeys.has(key)) continue;
          if (!deduped.has(key)) {
            deduped.set(key, {
              city,
              country,
              displayName,
              latitude,
              longitude,
            });
          }
        }

        if (mounted) {
          setStoreBackedCities(Array.from(deduped.values()));
        }
      } catch (error) {
        console.warn('[CitySelectorSidebar] Failed to parse store-backed cities', error);
      }
    };

    loadStoreCities();

    const handleStoreCreated = () => {
      loadStoreCities();
    };

    window.addEventListener('store-created', handleStoreCreated);

    return () => {
      mounted = false;
      window.removeEventListener('store-created', handleStoreCreated);
    };
  }, [isOpen]);

  const catalogOptions = useMemo<CatalogCityOption[]>(() => {
    const defaultOptions: CatalogCityOption[] = CITY_LANDMARK_LOCATIONS.map((locationItem) => ({
      city: locationItem.city,
      country: locationItem.country,
      displayName: locationItem.displayName,
      latitude: locationItem.latitude,
      longitude: locationItem.longitude,
      aliases: locationItem.aliases,
    }));

    return [...defaultOptions, ...storeBackedCities];
  }, [storeBackedCities]);

  const knownCityKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const option of catalogOptions) {
      keys.add(`${normalizeLocationText(option.city)}|${normalizeLocationText(option.country)}`);
      keys.add(normalizeLocationText(option.city));
    }
    return keys;
  }, [catalogOptions]);

  const groupedCityOptions = useMemo(() => {
    const query = normalizeLocationText(searchTerm);
    const byCountry = new Map<string, CatalogCityOption[]>();

    for (const locationItem of catalogOptions) {
      const searchableText = [
        locationItem.city,
        locationItem.country,
        locationItem.displayName,
        ...(locationItem.aliases ?? []),
      ].join(' ');

      if (query && !normalizeLocationText(searchableText).includes(query)) {
        continue;
      }

      if (!byCountry.has(locationItem.country)) {
        byCountry.set(locationItem.country, []);
      }

      byCountry.get(locationItem.country)!.push(locationItem);
    }

    return Array.from(byCountry.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([country, locations]) => ({
        country,
        locations: [...locations].sort((a, b) => a.city.localeCompare(b.city)),
      }));
  }, [catalogOptions, searchTerm]);

  useEffect(() => {
    if (!isOpen) return;

    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      setSearchTerm('');
      setGlobalResults([]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const query = searchTerm.trim();
    if (query.length < 1) {
      setGlobalResults([]);
      setIsSearchingGlobal(false);
      return;
    }

    const controller = new AbortController();
    const debounceId = window.setTimeout(async () => {
      setIsSearchingGlobal(true);
      try {
        const normalizedQuery = normalizeLocationText(query);
        const queryVariants = Array.from(new Set([query, normalizedQuery])).filter(Boolean);

        const responses = await Promise.all(
          queryVariants.map(async (variant) => {
            const response = await fetch(
              `https://nominatim.openstreetmap.org/search?format=jsonv2&q=${encodeURIComponent(variant)}&limit=25&addressdetails=1&accept-language=es`,
              { signal: controller.signal }
            );

            if (!response.ok) {
              throw new Error(`Nominatim status ${response.status}`);
            }

            return (await response.json()) as NominatimSearchResult[];
          })
        );

        const data = responses.flat();
        const placeTypes = new Set(['city', 'town', 'village', 'hamlet', 'municipality', 'suburb', 'borough', 'quarter']);

        const normalized = data
          .filter((item) => {
            const type = (item.addresstype || item.type || '').toLowerCase();
            const cityFromAddress = Boolean(
              item.address?.city ||
              item.address?.town ||
              item.address?.village ||
              item.address?.hamlet ||
              item.address?.municipality ||
              item.address?.suburb
            );
            const matchesCategory = item.category === 'place' || item.category === 'boundary';
            return (matchesCategory && placeTypes.has(type)) || cityFromAddress;
          })
          .map((item) => {
            const city = extractCityName(item);
            const country = item.address?.country || 'Pais no especificado';
            return {
              id: String(item.place_id),
              city,
              country,
              displayName: `${city}, ${country}`,
              fullDisplayName: item.display_name,
              latitude: Number(item.lat),
              longitude: Number(item.lon),
            };
          })
          .filter((item) => Number.isFinite(item.latitude) && Number.isFinite(item.longitude))
          .filter((item) => {
            const haystack = normalizeLocationText(`${item.city} ${item.country} ${item.fullDisplayName}`);
            return haystack.includes(normalizedQuery);
          });

        const deduped = normalized.filter((item, index, arr) => {
          return arr.findIndex((candidate) => candidate.city === item.city && candidate.country === item.country) === index;
        });

        setGlobalResults(deduped.slice(0, 18));
      } catch (error) {
        if ((error as DOMException)?.name !== 'AbortError') {
          console.warn('[CitySelectorSidebar] Global search failed', error);
          setGlobalResults([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsSearchingGlobal(false);
        }
      }
    }, 300);

    return () => {
      window.clearTimeout(debounceId);
      controller.abort();
    };
  }, [isOpen, searchTerm]);

  const handleSelectCity = (selection: SelectedCityLocation) => {
    onSelectCity(selection);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            aria-label="Cerrar selector de ciudad"
            className="fixed inset-0 bg-black/45 z-[9997]"
            onClick={onClose}
          />

          <motion.aside
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.2 }}
            className="fixed right-0 w-full lg:w-[400px] bg-background shadow-2xl z-[9998] flex flex-col overflow-x-hidden"
            style={{
              top: isDesktop ? `${headerHeight}px` : 0,
              bottom: !isDesktop ? 0 : undefined,
              height: isDesktop ? `calc(100vh - ${headerHeight}px)` : '100dvh',
            }}
          >
            {!isDesktop && <div className="h-[env(safe-area-inset-top,0px)] bg-primary w-full shrink-0" />}

            <div className="flex items-center justify-between p-4 border-b shrink-0 bg-primary text-primary-foreground min-h-[64px]">
              <h2 className="text-lg font-semibold">Elegir ciudad</h2>
              <div className="flex items-center gap-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onClose();
                        }}
                        className="p-1 hover:bg-white/10 text-white rounded-lg transition-colors hover:text-white"
                      >
                        <X className="w-5 h-5 text-white" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent variant="sidebar">
                      <p>Cerrar</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>

            <div className="p-4 border-b border-border bg-background">
              <Input
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Filtrar por país o ciudad"
                className="h-11"
              />
            </div>

            <ScrollArea ref={scrollAreaRef} className="flex-1 overflow-x-hidden">
              <div className="p-4 space-y-4">
                {searchTerm.trim().length >= 1 && (
                  isSearchingGlobal ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground px-1 py-4">
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Buscando ciudades en todo el mundo...
                    </div>
                  ) : globalResults.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                      Sin coincidencias para esta búsqueda.
                    </div>
                  ) : (() => {
                    const byCountry = new Map<string, GlobalLocationResult[]>();
                    for (const result of globalResults) {
                      if (!byCountry.has(result.country)) byCountry.set(result.country, []);
                      byCountry.get(result.country)!.push(result);
                    }
                    return Array.from(byCountry.entries()).map(([country, results]) => (
                      <div key={country} className="rounded-xl border border-border bg-card p-3">
                        <p className="text-sm font-semibold text-card-foreground mb-2">{country}</p>
                        <div className="flex flex-wrap gap-2">
                          {results.map((result) => {
                            const resultKey = `${normalizeLocationText(result.city)}|${normalizeLocationText(result.country)}`;
                            const isPredefined = knownCityKeys.has(resultKey) || knownCityKeys.has(normalizeLocationText(result.city));
                            return (
                            <button
                              key={result.id}
                              onClick={() => handleSelectCity({ ...result, isPredefined })}
                              className={cn(
                                'px-3 py-1.5 rounded-full text-sm border transition-colors',
                                selectedCity === result.city
                                  ? 'border-primary bg-primary text-primary-foreground font-semibold'
                                  : 'border-border bg-background hover:bg-muted text-foreground'
                              )}
                            >
                              {result.city}
                            </button>
                            );
                          })}
                        </div>
                      </div>
                    ));
                  })()
                )}

                {!searchTerm.trim() && groupedCityOptions.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
                    No encontramos ciudades para ese filtro.
                  </div>
                ) : !searchTerm.trim() ? (
                  groupedCityOptions.map((countryGroup) => (
                    <div key={countryGroup.country} className="rounded-xl border border-border bg-card p-3">
                      <p className="text-sm font-semibold text-card-foreground mb-2">{countryGroup.country}</p>
                      <div className="flex flex-wrap gap-2">
                        {countryGroup.locations.map((locationItem) => (
                          <button
                            key={`${countryGroup.country}-${locationItem.city}`}
                            onClick={() =>
                              handleSelectCity({
                                city: locationItem.city,
                                displayName: locationItem.displayName,
                                latitude: locationItem.latitude,
                                longitude: locationItem.longitude,
                                isPredefined: true,
                              })
                            }
                            className={cn(
                              'px-3 py-1.5 rounded-full text-sm border transition-colors',
                              selectedCity === locationItem.city
                                ? 'border-primary bg-primary text-primary-foreground font-semibold'
                                : 'border-border bg-background hover:bg-muted text-foreground'
                            )}
                          >
                            {locationItem.city}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                ) : null}
              </div>
            </ScrollArea>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CitySelectorSidebar;